import React from 'react';
import { MapPin, Clock, Star, Navigation } from 'lucide-react';
import { Shop } from '../types/Shop';
import TagBadge from './TagBadge';
import { getShopStatus } from '../utils/shop';
import { getDirectionsToShop } from '../utils/directions';
import { isShopExpired, calculateDistance, getExpirationInfo, getTodaysOpeningHours } from '../utils/shop';
import { cn } from "@/lib/utils";

interface ShopListContentProps {
    shops: Shop[];
    userLocation: [number, number] | null;
    isDarkMode?: boolean;
    onShopClick?: (shop: Shop) => void;
    className?: string;
}

/**
 * Reusable shop list content that can be used in:
 * - Side panel (tablet/desktop)
 * - Drawer (mobile)
 * - Modal (legacy)
 */
export default function ShopListContent({
    shops,
    userLocation,
    isDarkMode = false,
    onShopClick,
    className,
}: ShopListContentProps) {
    const [currentTime, setCurrentTime] = React.useState(new Date());

    // Update current time every 30 minutes for countdown display
    React.useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1800000); // Update every 30 minutes

        return () => clearInterval(interval);
    }, []);

    // Filter and sort shops
    const sortedShops = React.useMemo(() => {
        // Filter out expired custom shops
        const visibleShops = shops.filter(shop => !isShopExpired(shop, currentTime));

        // Sort shops by distance if user location is available
        return userLocation
            ? [...visibleShops].sort((a, b) => {
                if (!a.coordinates || !Array.isArray(a.coordinates) || a.coordinates.length < 2) return 1;
                if (!b.coordinates || !Array.isArray(b.coordinates) || b.coordinates.length < 2) return -1;

                const distanceA = calculateDistance(userLocation[0], userLocation[1], a.coordinates[0], a.coordinates[1]);
                const distanceB = calculateDistance(userLocation[0], userLocation[1], b.coordinates[0], b.coordinates[1]);
                return distanceA - distanceB;
            })
            : visibleShops;
    }, [shops, userLocation, currentTime]);

    if (sortedShops.length === 0) {
        return (
            <div className={cn(
                "text-center py-12 px-4",
                isDarkMode ? "text-gray-400" : "text-gray-600",
                className
            )}>
                <p className="text-lg mb-2">No shops found</p>
                <p className="text-sm">Try adjusting your filters or search area</p>
            </div>
        );
    }

    return (
        <div className={cn("space-y-3 p-3", className)}>
            {sortedShops.map((shop) => {
                const distance = userLocation
                    ? calculateDistance(userLocation[0], userLocation[1], shop.coordinates[0], shop.coordinates[1])
                    : null;
                const { status } = getShopStatus(shop.openingHours);

                return (
                    <div
                        key={shop.id}
                        onClick={() => onShopClick?.(shop)}
                        className={cn(
                            "rounded-xl shadow-sm border p-3 transition-colors",
                            onShopClick && "cursor-pointer hover:shadow-md",
                            isDarkMode
                                ? "bg-gray-800/50 border-gray-700 hover:bg-gray-800"
                                : "bg-white border-gray-100 hover:bg-gray-50"
                        )}
                    >
                        <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                                <h3 className={cn(
                                    "font-bold text-base mb-1 truncate",
                                    isDarkMode ? "text-white" : "text-gray-900"
                                )}>
                                    {shop.name}
                                </h3>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <TagBadge
                                        tag={shop.type === "secondhand" ? "Second Hand" : shop.type === "custom" ? "Custom Shop" : "Flea Market"}
                                        type={shop.type}
                                    />

                                    {/* Show countdown for custom shops */}
                                    {shop.isCustom && shop.expiresAt && (
                                        (() => {
                                            const exp = getExpirationInfo(shop, currentTime);
                                            if (!exp) return null;
                                            return (
                                                <span className={cn(
                                                    "inline-block px-2 py-0.5 rounded-full text-xs font-medium",
                                                    exp.expired ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"
                                                )}>
                                                    {exp.text.replace("Expires in ", "")}
                                                </span>
                                            );
                                        })()
                                    )}
                                </div>
                            </div>
                            <span
                                className={cn(
                                    "px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ml-2",
                                    status === "open"
                                        ? "bg-green-100 text-green-800"
                                        : status === "closingSoon"
                                            ? "bg-yellow-100 text-yellow-800"
                                            : "bg-red-100 text-red-800"
                                )}
                            >
                                {status === "open" ? "Open" : status === "closingSoon" ? "Closing Soon" : "Closed"}
                            </span>
                        </div>

                        {shop.description && (
                            <p className={cn(
                                "text-sm mb-3 line-clamp-2",
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                            )}>
                                {shop.description}
                            </p>
                        )}

                        <div className="space-y-1.5 text-sm">
                            <div className={cn(
                                "flex items-center space-x-2",
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                            )}>
                                <MapPin className="h-4 w-4 flex-shrink-0" />
                                <span className="flex-1 truncate">{shop.address}</span>
                                {distance && (
                                    <span className="text-blue-500 font-medium shrink-0">
                                        {distance.toFixed(1)} km
                                    </span>
                                )}
                            </div>

                            <div className={cn(
                                "flex items-center space-x-2",
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                            )}>
                                <Clock className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">
                                    Today: {getTodaysOpeningHours(shop.openingHours, currentTime)}
                                </span>
                            </div>

                            {shop.rating && (
                                <div className={cn(
                                    "flex items-center space-x-2",
                                    isDarkMode ? "text-gray-300" : "text-gray-600"
                                )}>
                                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                                    <span>{shop.rating}/5</span>
                                </div>
                            )}
                        </div>

                        <div className={cn(
                            "flex items-center space-x-2 mt-3 pt-3 border-t",
                            isDarkMode ? "border-gray-700" : "border-gray-100"
                        )}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    getDirectionsToShop(shop);
                                }}
                                className="flex items-center space-x-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                            >
                                <Navigation className="h-3.5 w-3.5" />
                                <span>Directions</span>
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
